import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import Axios from "axios";
import BigNumber from "bignumber.js";
import { BASE18, FS, MAX_DATE, environment } from "src/configs/constance";
import { isNullAddress, shuffleArray } from "src/utils";
import BattleHandContract from "src/contract-logic/logic/BattleHandContract";
import multiCall from "src/utils/multicall";

const client = Axios.create({
  baseURL: environment == "mainnet" ? "https://backend-nft-app.trava.finance" : "http://206.189.33.190:3457",
});

export const PLAY_STEP = 7;
export const MIN_BNB_BET_PRICE = 0.01;
export const MIN_TRAVA_BET_PRICE = 5000;
export const ROOM_STATUS = { OPEN: 0, STANDBY: 1, READY: 2, FINISHED: 3 };
export const CARD = { ROCK: 0, PAPER: 1, SCISSOR: 2 };
export const RESULT = [
  [0, -1, 1],
  [1, 0, -1],
  [-1, 1, 0],
];

export const getLeaderboard = createAsyncThunk("battleHandSlice/getLeaderboard", async ({ sort }) => {
  const params = { sort, order: "-1", pageSize: "10", page: "1" };
  if (sort == "winRate") params.minRound = 10;
  const res = await client.get("/v1/battles/leaderboard", { params }).then((res) => res.data);
  return res;
});

export const searchLeaderboard = createAsyncThunk("battleHandSlice/searchLeaderboard", async ({ address }) => {
  const res = await client.get(`/v1/battles/leaderboard/${address}`).then((res) => res.data);
  return res;
});

export const loadMoreLeaderboard = createAsyncThunk("battleHandSlice/loadMoreLeaderboard", async ({ page, sort }) => {
  const params = { sort, order: "-1", page, pageSize: "10" };
  if (sort == "winRate") {
    params.minRound = 10;
  }
  const res = await client.get("/v1/battles/leaderboard", { params }).then((res) => res.data);
  return res;
});

export const getNonce = createAsyncThunk("battleHandSlice/getNonce", async ({ address }) => {
  const res = await client.post("/v1/auth/nonce", { address }).then((res) => res.data);
  const message = res.message;
  return message;
});

export const login = createAsyncThunk("battleHandSlice/login", async ({ address, signature }) => {
  const res = await client.post("/v1/auth/login", { address, signature }).then((res) => res.data);
  window.localStorage.setItem("jwtNft", res.token);
  return res.token;
});
export const saveSequence = createAsyncThunk(
  "battleHandSlice/saveSequence",
  async ({ password, sequence, signature, playerOne, betAmount, token, roomId }) => {
    const res = await client
      .post(
        "/v1/battles",
        { password, signature, address: playerOne, sequence, betAmount, token, roomId },
        { headers: { authorization: `Bearer ${window.localStorage.getItem("jwtNft")}` } }
      )
      .then((res) => res.data);
    return res;
  }
);

export const getSequence = createAsyncThunk("battleHandSlice/getSequence", async ({ roomId }) => {
  try {
    const res = await client
      .get(`v1/battles/${roomId}`, { headers: { authorization: `Bearer ${window.localStorage.getItem("jwtNft")}` } })
      .then((res) => res.data);
    return res.playerOneSequence;
  } catch (error) {
    const sequenceLocal = window.localStorage.getItem(`room${roomId}`);
    if (sequenceLocal) {
      return JSON.parse(sequenceLocal);
    }
  }
});

export const getJoiningTime = createAsyncThunk("battleHandSlice/getJoiningTime", async ({ roomId }) => {
  const res = await BattleHandContract.getJoiningTime(roomId);
  const timeout = await BattleHandContract.getTimeout();
  if (res && timeout) {
    return (res + timeout) * 1000;
  }
});

export const getUnclaimedBnbReward = createAsyncThunk("battleHandSlice/getUnclaimedBnbReward", async ({ address }) => {
  const res = await BattleHandContract.getUnclaimedBnbReward(address);
  return res;
});
export const getUnclaimedErc20Reward = createAsyncThunk(
  "battleHandSlice/getUnclaimedErc20Reward",
  async ({ address }) => {
    const res = await BattleHandContract.getUnclaimedErc20Reward(address, environment == "mainnet" ? 1 : 2);
    return res;
  }
);

export const fetchDetailRoom = createAsyncThunk("battleHandSlice/fetchDetailRoom", async ({ roomId }) => {
  const res = await BattleHandContract.getRoomInfo(roomId);
  if (isNullAddress(res.playerOne)) {
    throw new Error("Network error");
  }
  return res;
});

export const fetchListRoomHistory = createAsyncThunk("battleHandSlice/fetchListRoomHistory", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const [totalJoined, totalHosted, timeout] = await Promise.all([
    BattleHandContract.getJoinedRoomCount(accountAddress),
    BattleHandContract.getHostedRoomCount(accountAddress),
    BattleHandContract.getTimeout(),
  ]);
  thunkAPI.dispatch(updateTimeout(timeout));
  const [joinedRoomIds, hostedRoomIds] = await Promise.all([
    multiCall(
      BattleHandContract.ABI,
      new Array(totalJoined).fill(1).map((_, idx) => ({
        address: BattleHandContract.ADDRESS,
        name: "getJoinedRoomAtIndex",
        params: [accountAddress, idx],
      }))
    ),
    multiCall(
      BattleHandContract.ABI,
      new Array(totalHosted).fill(1).map((_, idx) => ({
        address: BattleHandContract.ADDRESS,
        name: "getHostedRoomAtIndex",
        params: [accountAddress, idx],
      }))
    ),
  ]);
  const joinedRoomIdsFlattened = joinedRoomIds.flat();
  const hostedRoomIdsFlattened = hostedRoomIds.flat();
  let roomIdsFlattened = joinedRoomIdsFlattened
    .concat(hostedRoomIdsFlattened)
    .sort((i1, i2) => BigNumber(i2._hex).comparedTo(BigNumber(i1._hex)));
  roomIdsFlattened = [...new Set(roomIdsFlattened)];
  thunkAPI.dispatch(updateTotaHistory({ total: roomIdsFlattened.length, list: [] }));
  let chunk = 500,
    roomIdListChunkPromises = [],
    n = roomIdsFlattened.length;
  for (let i = 0; i < n; i += chunk) {
    let roomIdSlice = roomIdsFlattened.slice(i, i + chunk);
    roomIdListChunkPromises.push(thunkAPI.dispatch(fetchBatchListHistoryRoom({ roomIdSlice })));
  }
  await Promise.all(roomIdListChunkPromises);
});

export const fetchBatchListHistoryRoom = createAsyncThunk(
  "battleHandSlice/fetchBatchListHistoryRoom",
  async ({ roomIdSlice }, thunkAPI) => {
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const [roomInfo, joiningTime] = await Promise.all([
      multiCall(
        BattleHandContract.ABI,
        roomIdSlice.map((roomId) => ({
          address: BattleHandContract.ADDRESS,
          name: "getRoomInfo",
          params: [roomId],
        }))
      ),
      multiCall(
        BattleHandContract.ABI,
        roomIdSlice.map((roomId) => ({
          address: BattleHandContract.ADDRESS,
          name: "getJoiningTime",
          params: [roomId],
        }))
      ),
    ]);
    const roomFlattened = roomInfo.flat();
    const joiningTimeFlattened = joiningTime.flat();
    return roomFlattened.map((record, idx) => {
      const win =
        record.playerOne.toUpperCase() == accountAddress.toUpperCase() ? record.result == 1 : record.result == 2;
      const timeout = thunkAPI.getState().battleHandSlice.timeout;
      return {
        roomId: parseInt(roomIdSlice[idx]),
        playerOne: record.playerOne,
        playerTwo: record.playerTwo,
        status: record.status,
        betAmount: BigNumber(record.betAmount._hex).dividedBy(BASE18).toNumber(),
        option: parseInt(record.option),
        result: parseInt(record.result),
        win,
        playerOneSequence: record.playerOneSequence.length ? record.playerOneSequence.map((i) => parseInt(i)) : null,
        playerTwoSequence: record.playerTwoSequence.length ? record.playerTwoSequence.map((i) => parseInt(i)) : null,
        claimableTime:
          joiningTimeFlattened[idx]._hex == "0x00"
            ? MAX_DATE
            : parseInt(joiningTimeFlattened[idx]._hex) * 1000 + timeout * 1000,
      };
    });
  }
);

export const fetchListRoom = createAsyncThunk("battleHandSlice/fetchListRoom", async (_, thunkAPI) => {
  const [total, timeout] = await Promise.all([
    BattleHandContract.getActiveRoomCount(),
    BattleHandContract.getTimeout(),
  ]);
  thunkAPI.dispatch(updateTotalRoom({ total, list: [] }));
  thunkAPI.dispatch(updateTimeout(timeout));
  const [roomIds] = await Promise.all([
    multiCall(
      BattleHandContract.ABI,
      new Array(total).fill(1).map((_, idx) => ({
        address: BattleHandContract.ADDRESS,
        name: "getActiveRoomAtIndex",
        params: [idx],
      }))
    ),
  ]);
  const roomIdsFlattened = roomIds.flat();
  shuffleArray(roomIdsFlattened);
  let chunk = 500,
    roomIdListChunkPromises = [],
    n = roomIdsFlattened.length;
  for (let i = 0; i < n; i += chunk) {
    let roomIdSlice = roomIdsFlattened.slice(i, i + chunk);
    roomIdListChunkPromises.push(thunkAPI.dispatch(fetchBatchListRoom({ roomIdSlice })));
  }
  await Promise.all(roomIdListChunkPromises);
});

export const fetchBatchListRoom = createAsyncThunk(
  "battleHandSlice/fetchBatchListRoom",
  async ({ roomIdSlice }, thunkAPI) => {
    const [roomInfo, joiningTime] = await Promise.all([
      multiCall(
        BattleHandContract.ABI,
        roomIdSlice.map((roomId) => ({
          address: BattleHandContract.ADDRESS,
          name: "getRoomInfo",
          params: [roomId],
        }))
      ),
      multiCall(
        BattleHandContract.ABI,
        roomIdSlice.map((roomId) => ({
          address: BattleHandContract.ADDRESS,
          name: "getJoiningTime",
          params: [roomId],
        }))
      ),
    ]);
    const roomFlattened = roomInfo.flat();
    const joiningTimeFlattened = joiningTime.flat();
    const timeout = thunkAPI.getState().battleHandSlice.timeout;
    return roomFlattened.map((record, idx) => {
      return {
        roomId: parseInt(roomIdSlice[idx]),
        playerOne: record.playerOne,
        playerTwo: isNullAddress(record.playerTwo) ? null : record.playerTwo,
        status: record.status,
        betAmount: BigNumber(record.betAmount._hex).dividedBy(BASE18).toNumber(),
        option: parseInt(record.option),
        claimableTime:
          joiningTimeFlattened[idx]._hex == "0x00"
            ? MAX_DATE
            : parseInt(joiningTimeFlattened[idx]._hex) * 1000 + timeout * 1000,
      };
    });
  }
);

export const listenEventInListRoom = createAsyncThunk("battleHandSlice/listenEventInListRoom", async (_, thunkAPI) => {
  BattleHandContract.initSocket();
  BattleHandContract.listenEventInListRoom(
    async (roomId) => {
      console.debug("event room created");
      const accountAddress = thunkAPI.getState().accountSlice.address;
      const room = await BattleHandContract.getRoomInfo(roomId);
      thunkAPI.dispatch(
        roomCreated({
          roomId,
          playerOne: room.playerOne,
          status: ROOM_STATUS.OPEN,
          betAmount: room.betAmount,
          option: room.option,
          result: 0,
          playerTwo: null,
          claimableTime: MAX_DATE,
        })
      );
      if (accountAddress && room.playerOne.toUpperCase() == accountAddress.toUpperCase()) {
        thunkAPI.dispatch(
          historyCreated({
            roomId,
            playerOne: room.playerOne,
            status: ROOM_STATUS.OPEN,
            betAmount: room.betAmount,
            option: room.option,
            result: 0,
            playerTwo: null,
            claimableTime: MAX_DATE,
          })
        );
      }
    },
    async (roomId) => {
      console.debug("event price updated");
      const room = await BattleHandContract.getRoomInfo(roomId);
      const betAmount = room.betAmount;
      thunkAPI.dispatch(updateRoomById({ roomId, betAmount }));
      thunkAPI.dispatch(updateHistoryById({ roomId, betAmount }));
      thunkAPI.dispatch(updateRoomDetail({ roomId, betAmount }));
    },
    async (roomId) => {
      console.debug("event room finished");
      const accountAddress = thunkAPI.getState().accountSlice.address;
      const roomDetail = thunkAPI.getState().battleHandSlice.roomDetail;
      const room = await BattleHandContract.getRoomInfo(roomId);
      thunkAPI.dispatch(removeRoomById({ roomId }));
      if (accountAddress) {
        if (
          room.playerOne.toUpperCase() == accountAddress.toUpperCase() ||
          room.playerTwo.toUpperCase() == accountAddress.toUpperCase() ||
          (roomDetail && roomDetail.roomId == roomId)
        ) {
          const win =
            room.playerOne.toUpperCase() == accountAddress.toUpperCase() ? room.result == 1 : room.result == 2;
          thunkAPI.dispatch(updateHistoryById({ ...room, win }));
          thunkAPI.dispatch(updateRoomDetail(room));
        }
      }
    },
    async (roomId) => {
      console.debug("event room closed");
      thunkAPI.dispatch(removeRoomById({ roomId }));
      thunkAPI.dispatch(updateHistoryById({ roomId, status: ROOM_STATUS.FINISHED }));
      thunkAPI.dispatch(updateRoomDetail({ roomId, status: ROOM_STATUS.FINISHED }));
    },
    async (roomId) => {
      console.debug("event join room");
      const room = await BattleHandContract.getRoomInfo(roomId);
      const accountAddress = thunkAPI.getState().accountSlice.address;
      const [timeout, joiningTime] = await Promise.all([
        BattleHandContract.getTimeout(),
        BattleHandContract.getJoiningTime(roomId),
      ]);
      const claimableTime = (timeout + joiningTime) * 1000;
      thunkAPI.dispatch(
        updateRoomById({
          roomId,
          status: ROOM_STATUS.READY,
          playerTwo: room.playerTwo,
          claimableTime,
        })
      );
      if (accountAddress) {
        if (room.playerTwo.toUpperCase() == accountAddress.toUpperCase()) {
          thunkAPI.dispatch(historyCreated({ ...room, claimableTime }));
        } else if (room.playerOne.toUpperCase() == accountAddress.toUpperCase()) {
          thunkAPI.dispatch(
            updateHistoryById({
              roomId,
              status: ROOM_STATUS.READY,
              playerTwo: room.playerTwo,
              claimableTime,
            })
          );
        }
        thunkAPI.dispatch(
          updateRoomDetail({
            roomId,
            status: ROOM_STATUS.READY,
            playerTwo: room.playerTwo,
            claimableTime,
          })
        );
      }
    },
    async (roomId) => {
      console.debug("event room left");
      thunkAPI.dispatch(updateHistoryById({ roomId, status: ROOM_STATUS.OPEN }));
      thunkAPI.dispatch(updateRoomById({ roomId, status: ROOM_STATUS.OPEN }));
      thunkAPI.dispatch(updateRoomDetail({ roomId, status: ROOM_STATUS.OPEN }));
    }
  );
});

const initialState = {
  fetchingStatus: FS.INITIAL,
  rooms: undefined,
  roomDetail: undefined,
  claimableTime: undefined,
  playerOneSequence: undefined,
  unclaimedBnbReward: undefined,
  unclaimedErc20Reward: undefined,
  history: undefined,
  timeout: undefined,
  leaderboard: undefined,
};

const battleHandSlice = createSlice({
  name: "battleHandSlice",
  initialState: initialState,
  reducers: {
    updateTotalRoom: (state, action) => {
      state.rooms = { ...state.rooms, ...action.payload };
    },
    updateTotaHistory: (state, action) => {
      state.history = { ...state.history, ...action.payload };
    },
    updatePlayerOneSequence: (state, action) => {
      state.playerOneSequence = action.payload;
    },
    roomCreated: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.rooms) {
        const index = state.rooms.list.findIndex((i) => i.roomId == roomId);
        if (index < 0) {
          state.rooms.list.unshift(action.payload);
          state.rooms.total += 1;
        }
      }
    },
    historyCreated: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.history) {
        const index = state.history.list.findIndex((i) => i.roomId == roomId);
        if (index < 0) {
          state.history.list.unshift(action.payload);
          state.history.total += 1;
        }
      }
    },
    updateRoomDetail: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.roomDetail && state.roomDetail.roomId == roomId) {
        state.roomDetail = { ...state.roomDetail, ...action.payload };
      }
    },
    updateRoomById: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.rooms) {
        const index = state.rooms.list.findIndex((i) => i.roomId == roomId);
        if (index >= 0) {
          const room = state.rooms.list[index];
          state.rooms.list[index] = { ...room, ...action.payload };
        }
      }
    },
    updateHistoryById: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.history) {
        const index = state.history.list.findIndex((i) => i.roomId == roomId);
        if (index >= 0) {
          const room = state.history.list[index];
          state.history.list[index] = { ...room, ...action.payload };
        }
      }
    },
    removeRoomById: (state, action) => {
      const roomId = action.payload.roomId;
      if (state.rooms) {
        const index = state.rooms.list.findIndex((i) => i.roomId == roomId);
        if (index >= 0) {
          state.rooms.list.splice(index, 1);
          state.rooms.total -= 1;
        }
      }
    },
    updateTimeout: (state, action) => {
      state.timeout = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchBatchListRoom.fulfilled, (state, action) => {
        if (!state.rooms.list) {
          state.rooms.list = action.payload;
        } else {
          state.rooms.list = state.rooms.list.concat(action.payload);
        }
      })
      .addCase(fetchListRoom.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchListRoomHistory.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchBatchListHistoryRoom.fulfilled, (state, action) => {
        if (!state.history.list) {
          state.history.list = action.payload;
        } else {
          state.history.list = state.history.list.concat(action.payload);
        }
      })
      .addCase(fetchBatchListRoom.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchDetailRoom.fulfilled, (state, action) => {
        state.roomDetail = {
          ...state.roomDetail,
          ...action.payload,
        };
      })
      .addCase(fetchDetailRoom.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(getSequence.fulfilled, (state, action) => {
        state.playerOneSequence = action.payload;
      })
      .addCase(getLeaderboard.fulfilled, (state, action) => {
        state.leaderboard = action.payload;
      })
      .addCase(getJoiningTime.fulfilled, (state, action) => {
        state.roomDetail = {
          ...state.roomDetail,
          claimableTime: action.payload,
        };
      })
      .addCase(getUnclaimedBnbReward.fulfilled, (state, action) => {
        state.unclaimedBnbReward = action.payload;
      })
      .addCase(getUnclaimedErc20Reward.fulfilled, (state, action) => {
        state.unclaimedErc20Reward = action.payload;
      })
      .addCase(loadMoreLeaderboard.fulfilled, (state, action) => {
        state.leaderboard = { ...action.payload, docs: state.leaderboard.docs.concat(action.payload.docs) };
      }),
});

export const {
  updateTotalRoom,
  updateTotaHistory,
  removeRoomById,
  updatePlayerOneSequence,
  updateRoomById,
  updateRoomDetail,
  updateHistoryById,
  roomCreated,
  historyCreated,
  updateTimeout,
  reset,
} = battleHandSlice.actions;

// selectors
export const selectBattleHandSlice = (state) => state.battleHandSlice;
export const selectListRoom = createDraftSafeSelector(
  (state) => selectBattleHandSlice(state).rooms,
  (rooms) => {
    if (!rooms) {
      return null;
    }
    return rooms.list || [];
  }
);

export const selectListRoomHistory = createDraftSafeSelector(
  (state) => selectBattleHandSlice(state).history,
  (rooms) => {
    if (!rooms) {
      return null;
    }
    return rooms.list || [];
  }
);

export const selectPlayerOneSequence = (state) => {
  return (
    state.battleHandSlice?.roomDetail?.playerOneSequence ||
    state.battleHandSlice?.playerOneSequence ||
    new Array(PLAY_STEP).fill(null)
  );
};

export default battleHandSlice.reducer;
